
import { Type } from "@google/genai";
import { GroundingLink, WeatherData, NewsResponse, RainfallWarning, MapZone } from "../types";
import { callMaiaRouter, get_maia_search_results } from "./maiaService";
import { getAiProvider, setAiProvider, AiProvider } from "./aiConfig";
import { fetchTegalWeather, analyzeWeatherStatus, analyzeRainfallWarning, fetchMarineForecast } from "./weatherService";

const NEWS_CACHE_DURATION = 15 * 60 * 1000; 
const WEATHER_CACHE_DURATION = 30 * 60 * 1000;
const WARNING_CACHE_DURATION = 15 * 60 * 1000; // Faster update for warning
// const BMKG_WARNING_URL = 'https://staklim-jateng.bmkg.go.id/pdcht.php';

const callGeminiSecurely = async (contents: any, config?: any, model = 'gemini-2.5-flash') => {
  // Ensure contents is in the correct format for REST API
  let formattedContents = contents;
  if (typeof contents === 'string') {
    formattedContents = [{ role: 'user', parts: [{ text: contents }] }];
  }

  const performRequest = async () => {
    const response = await fetch('/api/gemini', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contents: formattedContents, config, model })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // Extract detailed error message
      const errorMessage = errorData.error?.message || errorData.error || response.statusText;
      const error: any = new Error(typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : errorMessage);
      error.status = response.status;
      error.details = errorData;
      throw error;
    }
    
    return await response.json();
  };

  // Retry logic for 503/overloaded
  const maxRetries = 3;
  let lastError: any;
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await performRequest();
    } catch (error: any) {
      lastError = error;
      if (error?.message?.includes('503') || error?.status === 503 || error?.message?.includes('overloaded')) {
        const delay = Math.pow(2, i) * 1000;
        await wait(delay);
        continue;
      }
      throw error;
    }
  }
  throw lastError;
};

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export { getAiProvider, setAiProvider };
export type { AiProvider };

// Helper for timeout fetch
const fetchWithTimeout = async (url: string, options: RequestInit = {}, timeout = 5000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    throw error;
  }
};

// Helper function to call Gemini with retries for 503/overloaded errors
// Deprecated: logic moved to callGeminiSecurely
// async function generateWithRetry(call: () => Promise<any>, maxRetries = 3): Promise<any> { ... }

// In-memory cache to replace localStorage for better realtime behavior
const memoryCache = new Map<string, { data: any, timestamp: number }>();

const getCachedData = (key: string, duration: number) => {
  const cached = memoryCache.get(key);
  if (!cached) return null;
  
  const isStale = Date.now() - cached.timestamp > duration;
  
  if (Array.isArray(cached.data)) {
    const arr = [...cached.data];
    Object.assign(arr, { isStale, updatedAt: cached.timestamp });
    return arr;
  }
  
  if (isStale) return { ...cached.data, isStale: true };
  return { ...cached.data, updatedAt: cached.timestamp };
};

const setCachedData = (key: string, data: any) => {
  memoryCache.set(key, { data, timestamp: Date.now() });
};

export const fetchRainfallWarning = async (forceRefresh: boolean = false): Promise<RainfallWarning> => {
  const cacheKey = 'rainfall_warning_v2_om'; // New cache key for OpenMeteo based warning
  const cached = getCachedData(cacheKey, WARNING_CACHE_DURATION);
  if (!forceRefresh && cached && !cached.isStale) return cached;

  try {
    // Use OpenMeteo analysis instead of scraping BMKG via Gemini
    const warning = await analyzeRainfallWarning(forceRefresh);
    setCachedData(cacheKey, warning);
    return warning;
  } catch (error) {
    console.error("Rainfall Warning Analysis Error:", error);
    return {
      status: 'Aman',
      message: 'Analisis cuaca terkendala. Cek kembali nanti.',
      regions: [],
      updatedAt: Date.now()
    };
  }
};

export const fetchDisasterNews = async (location: string, forceRefresh: boolean = false): Promise<NewsResponse> => {
  const cacheKey = `news_v2_${location.replace(/\s/g, '_')}`;
  const cached = getCachedData(cacheKey, NEWS_CACHE_DURATION);
  
  if (!forceRefresh) {
    if (cached && !cached.isStale) {
      return { ...cached, isCached: true };
    }
  }

  const provider = getAiProvider();
  
  // Logic for MAIA_ONLY mode or AUTO fallback
  const tryMaia = async () => {
    // 1. Explicit search via search.maia.id first
    let contextData = "";
    const currentYear = new Date().getFullYear();
    try {
      contextData = await get_maia_search_results({
        query: `berita ${location} jawa tengah bencana banjir rob cuaca terkini ${currentYear}`,
        limit: 5,
        time_range: 'day'
      });
    } catch (e) {
      console.error("Maia Search error in news:", e);
    }

    const text = await callMaiaRouter([{
      role: "system",
      content: `Anda adalah reporter Siaga Tegal. Gunakan data konteks berikut untuk membuat laporan singkat.
      
      CONTEXT:
      ${contextData}
      `
    }, {
      role: "user",
      content: `Laporan singkat cuaca & banjir di wilayah ${location} (Jawa Tengah) dalam 24 jam terakhir (Tahun ${currentYear}). HANYA gunakan data terkini. JANGAN buat prediksi jangka panjang. Jika aman, katakan 'Kondisi Tegal saat ini kondusif'.`
    }], "maia/gemini-2.5-flash");

    // Extract URLs from contextData if possible, or just leave empty for now as simple strings
    // Extract Title and URL from contextData
    // Format matches maiaPlugin.ts output: Title: ... \nContent: ... \nURL: ...
    const linkRegex = /Title: (.*?)\nContent: [\s\S]*?\nURL: (https?:\/\/[^\s]+)/g;
    const links: GroundingLink[] = [];
    let match;
    while ((match = linkRegex.exec(contextData)) !== null) {
      links.push({ title: match[1].trim() || "Sumber Berita", uri: match[2] });
    }

    return {
      text: text + (provider === 'MAIA_ONLY' ? "" : "\n\n*(Info via Maia Search)*"),
      links: links.slice(0, 10), // Limit to top 10 sources
      updatedAt: Date.now()
    };
  };

  if (provider === 'MAIA_ONLY') {
    try {
      const result = await tryMaia();
      setCachedData(cacheKey, result);
      return result;
    } catch (e) {
      console.error("Maia Only Mode Failed:", e);
      // Fallback is intentionally disabled when in MAIA_ONLY mode to save Gemini credits
      if (cached) return { ...cached, isCached: true, isStale: true };
      return { 
        text: "⚠️ Layanan berita sementara tidak tersedia (Limit Tercapai). Silakan coba lagi nanti.", 
        links: [],
        updatedAt: Date.now()
      };
    }
  }

  try {
    const result = await callGeminiSecurely(
      `Laporan singkat cuaca & banjir di wilayah ${location} (Jawa Tengah) dalam 24 jam terakhir. Berikan poin-poin penting. Jika aman, katakan 'Kondisi Tegal saat ini kondusif'.`,
      { 
        tools: [{ googleSearch: {} }],
        temperature: 0.1 
      },
      'gemini-2.5-flash'
    );

    // Secure API returns raw response structure
    const text = result.candidates?.[0]?.content?.parts?.[0]?.text || "Kondisi Tegal saat ini terpantau kondusif.";
    
    // Parse grounding metadata if available
    const chunks = result.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
    const links: GroundingLink[] = chunks
      .filter((chunk: any) => chunk.web)
      .map((chunk: any) => ({ title: chunk.web.title || "Sumber", uri: chunk.web.uri }));

    const warning = { text, links, updatedAt: Date.now() };
    setCachedData(cacheKey, warning);
    return warning;
  } catch (error: any) {
    console.warn("Primary Gemini News Error, trying Maia fallback:", error);
    try {
      return await tryMaia();
    } catch (maiaError) {
      console.error("Maia fallback failed:", maiaError);
    }

    if (cached) return { ...cached, isCached: true, isStale: true };
    return { 
      text: "⚠️ Server pusat sedang sangat sibuk. Kami menampilkan data terakhir atau silakan coba tekan tombol perbarui dalam beberapa saat.", 
      links: [],
      updatedAt: Date.now()
    };
  }
};

export const fetchQuickActionInfo = async (type: 'rob' | 'weather', location?: string): Promise<string> => {
  // Use local analysis for weather to save AI tokens and be faster
  if (type === 'weather') {
    try {
      const weather = await fetchTegalWeather(location || 'Slawi');
      const analysis = analyzeWeatherStatus(weather);
      return analysis;
    } catch (e) {
      console.warn("Local weather analysis failed, falling back to AI", e);
    }
  }

  // Use local analysis for rob/marine to ensure real-time data
  if (type === 'rob') {
    try {
      const marine = await fetchMarineForecast();
      if (marine) {
        const time = new Date(marine.updatedAt).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
        let status = `Pantauan Laut Tegal (Update ${time}):\n`;
        status += `- Tinggi Ombak: ${marine.waveHeight} meter\n`;
        status += `- Arah Ombak: ${marine.waveDirection}°\n`;
        
        if (marine.isRobPotential) {
          status += `\n⚠️ PERINGATAN: Potensi ROB tinggi! Ombak mencapai ${marine.waveHeight}m. Harap waspada di area pesisir.`;
        } else {
          status += `\n✅ Kondisi Laut Aman. Tidak ada potensi rob signifikan saat ini.`;
        }
        return status;
      }
    } catch (e) {
      console.warn("Local marine analysis failed, falling back to AI", e);
    }
  }

  const cacheKey = `quick_v2_${type}_${location?.replace(/\s/g, '') || 'default'}`;
  const provider = getAiProvider();
  
  // Prompt for AI
  const prompt = type === 'rob' 
    ? "Status pasang laut Pelabuhan Tegal & Muarareja saat ini. Apakah ada potensi rob?"
    : `Kondisi cuaca di ${location || 'Slawi, Bojong, Bumijawa'} saat ini. Apakah hujan lebat?`;

  // MAIA_ONLY Handling
  if (provider === 'MAIA_ONLY') {
    try {
      const text = await callMaiaRouter([{ role: "user", content: prompt }]);
      setCachedData(cacheKey, text);
      return text;
    } catch (maiaError) {
      console.error("Maia QuickInfo failed:", maiaError);
      const stale = getCachedData(cacheKey, 24 * 60 * 60 * 1000);
      if (stale) return stale.text || stale;
      return "Info tidak tersedia (Limit).";
    }
  }

  try {
    const result = await callGeminiSecurely(
      prompt,
      { tools: [{ googleSearch: {} }] },
      'gemini-2.5-flash'
    );
    const res = result.candidates?.[0]?.content?.parts?.[0]?.text || "Informasi tidak tersedia.";
    setCachedData(cacheKey, res);
    return res;
  } catch (error: any) {
    try {
      const text = await callMaiaRouter([{ role: "user", content: prompt }]);
      return text;
    } catch (maiaError) {
      console.error("Maia QuickInfo fallback failed:", maiaError);
    }

    const stale = getCachedData(cacheKey, 24 * 60 * 60 * 1000);
    if (stale) return stale.text || stale;
    return "Maaf, sistem sedang padat. Info cepat tidak bisa dimuat sementara.";
  }
};

// Weather fetching logic moved to weatherService.ts

export const getSurvivalChecklist = async (disasterType: string): Promise<string[]> => {
  const provider = getAiProvider();
  
  if (provider === 'MAIA_ONLY') {
    try {
      const text = await callMaiaRouter([{
        role: "user",
        content: `Daftar isi tas siaga bencana ${disasterType}. Maksimal 7 item. Output ONLY JSON array of strings.`
      }], "maia/gemini-2.5-flash", true);
      return JSON.parse(text);
    } catch (maiaError) {
      console.error("Maia Checklist failed:", maiaError);
      return ["Air Minum", "Makanan Instan", "Senter", "P3K", "Uang Tunai", "Masker", "Dokumen"];
    }
  }

  try {
    const result = await callGeminiSecurely(
      `Daftar isi tas siaga bencana ${disasterType}. Maksimal 7 item. JSON array.`,
      {
        responseMimeType: "application/json",
        responseSchema: { type: Type.ARRAY, items: { type: Type.STRING } }
      },
      'gemini-2.5-flash'
    );
    const text = result.candidates?.[0]?.content?.parts?.[0]?.text || '[]';
    return JSON.parse(text);
  } catch (error) {
    try {
      const text = await callMaiaRouter([{
        role: "user",
        content: `Daftar isi tas siaga bencana ${disasterType}. Maksimal 7 item. Output ONLY JSON array of strings.`
      }], "maia/gemini-2.5-flash", true);
      return JSON.parse(text);
    } catch (maiaError) {
      console.error("Maia Checklist fallback failed:", maiaError);
    }

    return ["Air Minum", "Makanan Instan", "Senter", "P3K", "Uang Tunai", "Masker", "Dokumen"];
  }
};

export const chatWithAssistant = async (message: string, history: any[]) => {
  const provider = getAiProvider();
  const tryMaia = async () => {
    const text = await callMaiaRouter([{
      role: "system",
      content: "Anda adalah Teman Siaga Tegal. Gunakan logat Tegal sesekali jika perlu untuk keramahan. Bantu warga soal bencana."
    }, {
      role: "user",
      content: message
    }]);
    return text;
  };

  if (provider === 'MAIA_ONLY') {
    try {
      return await tryMaia();
    } catch (e) {
      console.error("Maia Chat Failed:", e);
      return "Maaf, Teman Siaga sedang istirahat sebentar (Limit). Silakan coba lagi nanti.";
    }
  }

  try {
    // Chat with Assistant (Stateless via secure API)
    // Note: We prepend system instruction because standard generateContent doesn't always support it easily in v1beta REST 
    // without specific field. We'll simulate it in the prompt for simplicity and robustness.
    const systemPrompt = "Anda adalah Teman Siaga Tegal. Gunakan logat Tegal sesekali jika perlu untuk keramahan. Bantu warga soal bencana.\n\n";
    
    // Use callGeminiSecurely which handles the REST API call
    const result = await callGeminiSecurely(
      [{ role: 'user', parts: [{ text: systemPrompt + message }] }],
      {}, // No special config needed
      'gemini-2.5-flash-lite'
    );
    
    return result.candidates?.[0]?.content?.parts?.[0]?.text || "Duh, sinyale putus-putus. Coba maning ya!";
  } catch (error: any) {
    try {
      return await tryMaia();
    } catch (maiaError) {
      console.error("Maia Chat fallback failed:", maiaError);
    }

    return "Duh, servernya lagi 'mbededeg' (penuh). Sabar ya, coba tanya lagi sedetik maning!";
  }
};

// Helper to fetch coordinates from OpenStreetMap (Nominatim)
// Use this to validate/update AI-guessed coordinates
const fetchCoordinatesFromOSM = async (locationName: string): Promise<{ lat: number, lng: number } | null> => {
  try {
    // Construct query: e.g. "Margasari Tegal" -> "Margasari+Tegal"
    const query = encodeURIComponent(`${locationName} Tegal`); 
    const url = `https://nominatim.openstreetmap.org/search?q=${query}&format=json&limit=1`;
    
    const response = await fetchWithTimeout(url, {
      headers: { 'User-Agent': 'SiagaTegal/1.0' }
    }, 3000); // 3s timeout per request

    if (!response.ok) return null;
    
    const data = await response.json();
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon)
      };
    }
    return null;
  } catch (e) {
    console.warn(`OSM Coordinate Fetch failed for ${locationName}:`, e);
    return null;
  }
};

const TEGAL_DISTRICTS = [
  // Kota Tegal
  { name: 'Tegal Barat', coords: { lat: -6.86, lng: 109.12 } },
  { name: 'Tegal Timur', coords: { lat: -6.87, lng: 109.14 } },
  { name: 'Tegal Selatan', coords: { lat: -6.89, lng: 109.13 } },
  { name: 'Margadana', coords: { lat: -6.87, lng: 109.10 } },
  // Kabupaten Tegal
  { name: 'Adiwerna', coords: { lat: -6.93, lng: 109.13 } },
  { name: 'Balapulang', coords: { lat: -7.07, lng: 109.13 } },
  { name: 'Bojong', coords: { lat: -7.15, lng: 109.18 } },
  { name: 'Bumijawa', coords: { lat: -7.22, lng: 109.16 } },
  { name: 'Dukuhturi', coords: { lat: -6.90, lng: 109.10 } },
  { name: 'Dukuhwaru', coords: { lat: -6.98, lng: 109.10 } },
  { name: 'Jatinegara', coords: { lat: -7.08, lng: 109.23 } },
  { name: 'Kedungbanteng', coords: { lat: -7.02, lng: 109.20 } },
  { name: 'Kramat', coords: { lat: -6.90, lng: 109.19 } },
  { name: 'Lebaksiu', coords: { lat: -7.05, lng: 109.15 } },
  { name: 'Margasari', coords: { lat: -7.13, lng: 109.05 } },
  { name: 'Pagerbarang', coords: { lat: -7.03, lng: 109.08 } },
  { name: 'Pangkah', coords: { lat: -6.98, lng: 109.16 } },
  { name: 'Slawi', coords: { lat: -6.98, lng: 109.14 } },
  { name: 'Suradadi', coords: { lat: -6.90, lng: 109.22 } },
  { name: 'Talang', coords: { lat: -6.91, lng: 109.14 } },
  { name: 'Tarub', coords: { lat: -6.93, lng: 109.17 } },
  { name: 'Warureja', coords: { lat: -6.91, lng: 109.26 } }
];

const ensureAllDistricts = (zones: any[]): MapZone[] => {
  const aiResults = new Map(zones.map((z: any) => [z.name.toLowerCase(), z]));
  
  return TEGAL_DISTRICTS.map(district => {
    const aiZone = aiResults.get(district.name.toLowerCase()) as any;
    
    if (aiZone) {
      // Use AI result but fallback to default coords if AI's coords are suspicious or missing
      return {
        ...aiZone,
        name: district.name, // Ensure consistent naming
        coords: (aiZone.coords && aiZone.coords.lat) ? aiZone.coords : district.coords
      };
    } else {
      // Default safe state for missing districts
      return {
        name: district.name,
        type: 'Aman',
        risk: 'Low',
        coords: district.coords,
        description: 'Kondisi terpantau aman dan kondusif.',
        referenceUrls: []
      };
    }
  });
};

const enhanceZonesWithOSM = async (zones: any[]): Promise<any[]> => {
  // We only want to fetch coordinates for zones that:
  // 1. Are NOT in our hardcoded TEGAL_DISTRICTS list (unknown locations)
  // 2. Do not have valid coordinates
  // 3. We process sequentially to avoid rate limiting
  
  const updatedZones = [];
  const districtNames = new Set(TEGAL_DISTRICTS.map(d => d.name.toLowerCase()));
  
  for (const zone of zones) {
    const isKnownDistrict = zone.name && districtNames.has(zone.name.toLowerCase());
    
    // Skip fetching if it's a known district (we already trust our static coords from ensureAllDistricts)
    if (isKnownDistrict) {
      updatedZones.push(zone);
      continue;
    }

    // Only fetch if name is specific enough and coordinates are missing/invalid
    // (Assuming valid coords are non-zero, though Tegal is around -6, 109)
    const hasValidCoords = zone.coords && zone.coords.lat !== 0 && zone.coords.lng !== 0;
    
    if (zone.name && zone.name.length > 3 && !hasValidCoords) {
      try {
        // Add delay to respect Nominatim rate limit (1 req/sec)
        await new Promise(resolve => setTimeout(resolve, 1200));
        
        const osmCoords = await fetchCoordinatesFromOSM(zone.name);
        if (osmCoords) {
          // Check if OSM coords are within reasonable Tegal region bounds
          const isValid = osmCoords.lat > -7.5 && osmCoords.lat < -6.5 && 
                          osmCoords.lng > 108.5 && osmCoords.lng < 109.5;
          
          if (isValid) {
            updatedZones.push({ ...zone, coords: osmCoords });
            continue;
          }
        }
      } catch (e) {
        // Ignore error and push original zone
      }
    }
    
    updatedZones.push(zone);
  }
  
  return updatedZones;
};

export const fetchDisasterMapData = async (): Promise<MapZone[]> => {
  const cacheKey = 'disaster_map_zones_v1';
  // Reduced cache duration to 30 seconds to support near real-time updates as requested
  const cached = getCachedData(cacheKey, 30 * 1000); 
  if (cached && !cached.isStale) return cached;

  const provider = getAiProvider();
  
  if (provider === 'MAIA_ONLY') {
    // Return safe defaults directly to avoid using credit for heavy map analysis
    // Or try Maia if really needed, but map analysis is token heavy.
    // Let's return defaults for now to be safe as per user request "gemini off kan dulu"
    // Actually, user said "gemini off", but maia might be fine?
    // Let's try Maia fallback logic directly
    try {
      console.warn("Using Maia Only for Map Data...");
      // For Maia, we simplify the prompt to save tokens/complexity
      const text = await callMaiaRouter([{
        role: "user",
        content: `Analisis potensi bencana di Kota Tegal & Kab Tegal hari ini. Output JSON Array of objects dengan properti: name (nama kecamatan), type (Aman/Banjir/Lainnya), risk (Low/Medium/High), coords (lat, lng), description. Wajib JSON.`
      }], "maia/gemini-2.5-flash", true);

      let zones = JSON.parse(text);
      if (!Array.isArray(zones)) zones = [];
      const finalZones = ensureAllDistricts(zones);
      setCachedData(cacheKey, finalZones);
      return finalZones;
    } catch (e) {
      console.error("Maia Only Map Data Failed:", e);
      return ensureAllDistricts([]);
    }
  }

  try {
    // 1. Fetch Search Results via Maia Plugin (search.maia.id) as requested by user
    let searchContext = "";
    const currentYear = new Date().getFullYear();
    try {
      searchContext = await get_maia_search_results({
        query: `bencana terkini Kota Tegal Kabupaten Tegal ${currentYear} banjir rob longsor kebakaran angin kencang`,
        limit: 10,
        time_range: 'week'
      });
    } catch (searchError) {
      console.error("Maia Search Failed:", searchError);
      // Fallback: searchContext will be empty, AI might rely on internal knowledge or fail gracefully
    }

    const result = await callGeminiSecurely(
      `Context Data (Hasil Pencarian Berita):
      ${searchContext}

      DAFTAR WILAYAH (Wajib dicek satu per satu):
      ${TEGAL_DISTRICTS.map(d => `- ${d.name}`).join('\n')}

      Tugas: Analisis data konteks untuk menentukan status bencana/cuaca di SETIAP wilayah kecamatan di atas.
      
      Output JSON Array of objects dengan format:
      {
        "name": "Nama Lokasi (Sesuai daftar)",
        "type": "Banjir" | "Rob" | "Longsor" | "Kebakaran" | "Lainnya" | "Aman",
        "risk": "High" | "Medium" | "Low",
        "coords": { "lat": number, "lng": number },
        "description": "Keterangan singkat (maks 20 kata). Jika Aman, tulis 'Kondisi terpantau aman'.",
        "referenceUrls": ["Link 1"] // Opsional
      }

      PENTING: 
      1. HANYA GUNAKAN DATA TAHUN ${currentYear}.
      2. Wajib mengembalikan status untuk SEMUA 22 Kecamatan di daftar. Jangan ada yang terlewat.
      3. Jika tidak ada berita spesifik untuk suatu kecamatan, tandai sebagai "Aman" dengan risk "Low".
      4. Gunakan koordinat default jika tidak ada update spesifik.
      5. Jika ada potensi umum (misal musim hujan), set risk "Medium" dengan type "Lainnya" (Waspada Hujan).
      
      Output ONLY RAW JSON.`,
      {
        // Disable googleSearch since we use explicit context
        tools: [],
      },
      'gemini-2.5-flash'
    );

    let text = result.candidates?.[0]?.content?.parts?.[0]?.text || '[]';
    text = text.replace(/```json\n?|\n?```/g, '').trim();
    let zones = JSON.parse(text);
    
    if (!Array.isArray(zones)) {
      console.warn("Map Data is not an array, defaulting to empty list", zones);
      zones = [];
    }

    // 3. Merge with default list to ensure completeness (in case AI missed some)
    const finalZones = ensureAllDistricts(zones);

    // 2. Enhance/Verify Coordinates using OSM Search (Optional for confirmed disasters)
    // We can skip this for "Aman" zones to save time
    const updatedZones = await enhanceZonesWithOSM(finalZones);

    setCachedData(cacheKey, updatedZones);
    return updatedZones;
  } catch (error) {
    console.error("Map Data Fetch Error:", error);
    
    // Try Maia Fallback
    try {
      console.warn("Attempting Maia Fallback for Map Data...");
      const text = await callMaiaRouter([{
        role: "user",
        content: `Analisis data bencana terkini di Tegal. Output JSON Array of objects: name, type, risk, coords (lat,lng), description, referenceUrls. Gunakan data umum jika tidak ada konteks spesifik.`
      }], "maia/gemini-2.5-flash", true);

      let zones = JSON.parse(text);
      if (!Array.isArray(zones)) zones = [];
      
      const finalZones = ensureAllDistricts(zones);
      const updatedZones = await enhanceZonesWithOSM(finalZones);
      setCachedData(cacheKey, updatedZones);
      return updatedZones;
    } catch (maiaError) {
      console.error("Maia Map fallback failed:", maiaError);
    }

    // Fallback to default zones if AI fails
    return cached || ensureAllDistricts([]);
  }
};
